import React from 'react';
import type { TFunction } from 'i18next';
import { type Column, type RowAction, Icon, Tooltip } from 'design-system-web';
import Pill from 'components/general/Pill';
import { ICON_SET_FEATHER } from 'lane-shared/helpers/constants/icons';
import { simpleDate } from 'lane-shared/helpers/formatters';
import type { InspectionTemplate } from 'inspection-types';

import styles from './templateLibraryTableConfig.scss';

export type TemplateLibraryTableRow = {
  id: string;
  name: string;
  lastUpdated: string | null;
  version: number;
  sectionsCount: number;
  questionsCount: number;
  hidden: boolean;
};

export function mapTemplateToTableRow(
  template: InspectionTemplate
): TemplateLibraryTableRow {
  return {
    id: template.id,
    name: template.metadata.name,
    lastUpdated: template.metadata.updatedAt ?? null,
    version: template.version,
    sectionsCount: template.sections.length,
    questionsCount: template.sections.reduce(
      (total, section) => total + section.items.length,
      0
    ),
    hidden: template.hidden,
  };
}

/**
 * A column that also carries a plain-text title. `Column.header` accepts a
 * ReactNode, but the Edit columns panel and CSV export both need a string —
 * `getCsvColumnHeader` reads `csvHeader` first and falls back to `''` for a
 * JSX header, which would leave the row unlabelled.
 */
type ColumnWithCsvHeader = Column<TemplateLibraryTableRow> & {
  csvHeader: string;
};

export function getTemplateLibraryColumns(
  t: TFunction,
  options?: { includeVisibilityColumn?: boolean }
): Column<TemplateLibraryTableRow>[] {
  const visibilityLabel = t(
    'web.admin.inspections.templateLibrary.columns.visibility'
  );

  const visibilityColumn: ColumnWithCsvHeader = {
    key: 'hidden',
    csvHeader: visibilityLabel,
    header: (
      <span className={styles.visibilityHeader}>
        {visibilityLabel}
        <Tooltip
          placement="top"
          TooltipComponent={t(
            'web.admin.inspections.templateLibrary.columns.visibilityTooltip'
          )}
        >
          <Icon
            name="info"
            set={ICON_SET_FEATHER}
            className={styles.infoIcon}
          />
        </Tooltip>
      </span>
    ),
    disableSorting: true,
    renderCell: (value: boolean) => (
      <div className={styles.visibilityCell}>
        {value ? (
          <Pill
            color="gray"
            iconName="eye-off"
            text={t('web.admin.inspections.templateLibrary.visibility.hidden')}
          />
        ) : (
          <Pill
            color="green"
            iconName="eye"
            text={t('web.admin.inspections.templateLibrary.visibility.visible')}
          />
        )}
      </div>
    ),
    renderForCSV: (value: boolean) =>
      t(
        value
          ? 'web.admin.inspections.templateLibrary.visibility.hidden'
          : 'web.admin.inspections.templateLibrary.visibility.visible'
      ),
  };

  return [
    {
      header: t('web.admin.inspections.templateLibrary.columns.name'),
      key: 'name',
      type: 'text',
    },
    ...(options?.includeVisibilityColumn ? [visibilityColumn] : []),
    {
      header: t('web.admin.inspections.templateLibrary.columns.lastUpdated'),
      key: 'lastUpdated',
      renderCell: (value: string | null) =>
        value ? simpleDate(value) : t('punctuation.dash'),
    },
    {
      header: t('web.admin.inspections.templateLibrary.columns.version'),
      key: 'version',
      type: 'text',
      disableSorting: true,
    },
    {
      header: t('web.admin.inspections.templateLibrary.columns.sections'),
      key: 'sectionsCount',
      type: 'text',
    },
    {
      header: t('web.admin.inspections.templateLibrary.columns.questions'),
      key: 'questionsCount',
      type: 'text',
    },
  ];
}

export function getTemplateLibraryRowActions(
  t: TFunction,
  options?: {
    onCustomize?: (row: TemplateLibraryTableRow) => void;
    onToggleVisibility?: (row: TemplateLibraryTableRow) => void;
    includeVisibilityActions?: boolean;
  }
): RowAction<TemplateLibraryTableRow>[] {
  const actions: RowAction<TemplateLibraryTableRow>[] = [
    {
      label: t('web.admin.inspections.templateLibrary.actions.customize'),
      onClick: (row: TemplateLibraryTableRow) => {
        options?.onCustomize?.(row);
      },
    },
  ];

  if (options?.includeVisibilityActions) {
    actions.push(
      {
        label: t('web.admin.inspections.templateLibrary.actions.hide'),
        isHidden: (row: TemplateLibraryTableRow) => row.hidden,
        onClick: (row: TemplateLibraryTableRow) => {
          options?.onToggleVisibility?.(row);
        },
      },
      {
        label: t('web.admin.inspections.templateLibrary.actions.unhide'),
        isHidden: (row: TemplateLibraryTableRow) => !row.hidden,
        onClick: (row: TemplateLibraryTableRow) => {
          options?.onToggleVisibility?.(row);
        },
      }
    );
  }

  return actions;
}
